import { useEffect, useRef, useState, type FormEvent } from 'react'
import { normalizeBarcode } from '@sp/shared'
import { createCodeReader, type CodeReader } from '../lib/barcode.ts'
import { Sheet } from './Sheet.tsx'
import { Button, Paragraph, TextField } from './ui.tsx'

/** Pauza mezi snimky — cteni v zxing-wasm na starsim telefonu neni zadarmo. */
const SCAN_GAP_MS = 120
/** Kolik snimku po sobe smi cteni selhat, nez se ctecka vzda (~2 s). */
const MAX_FAILED_READS = 15

type CameraState = 'starting' | 'scanning' | 'denied' | 'unavailable' | 'no_reader'

const CAMERA_TEXT: Record<CameraState, string> = {
  starting: 'Zapínám kameru…',
  scanning: 'Namiř kameru na čárový kód.',
  denied: 'Appka nemá přístup ke kameře. Povol ho v nastavení prohlížeče, nebo číslo kódu napiš.',
  unavailable: 'Kamera tady nejde použít. Číslo kódu můžeš napsat.',
  no_reader: 'Čtečka kódů se nenačetla (bez signálu?). Číslo kódu můžeš napsat.',
}

export function BarcodeScanner({
  open,
  onClose,
  onCode,
  title = 'Naskenovat kód',
  hint,
}: {
  open: boolean
  onClose(): void
  /** Kod uz v ulozenem tvaru (normalizeBarcode). */
  onCode(code: string): void
  title?: string | undefined
  hint?: string | undefined
}) {
  return (
    <Sheet open={open} onClose={onClose} title={title} subtitle={hint}>
      {open && <ScannerBody onCode={onCode} />}
    </Sheet>
  )
}

/**
 * Kamera bezi jen po dobu, kdy je panel otevreny. Kod jde vzdy napsat i rucne —
 * pocitac kameru mit nemusi a pristup k ni muze byt zakazany.
 */
function ScannerBody({ onCode }: { onCode(code: string): void }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const onCodeRef = useRef(onCode)
  useEffect(() => {
    onCodeRef.current = onCode
  })
  const [camera, setCamera] = useState<CameraState>('starting')
  const [manual, setManual] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let stopped = false
    let stream: MediaStream | null = null
    let timer: number | undefined
    let failures = 0

    const stop = () => {
      stopped = true
      window.clearTimeout(timer)
      stream?.getTracks().forEach((track) => track.stop())
    }

    async function scan(video: HTMLVideoElement, reader: CodeReader) {
      if (stopped) return
      try {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          const [code] = await reader.read(video)
          failures = 0
          if (code && !stopped) {
            stop()
            navigator.vibrate?.(40)
            onCodeRef.current(code)
            return
          }
        }
      } catch {
        // Jeden necitelny snimek nevadi; kdyz selhava kazdy, ctecka nefunguje.
        failures += 1
        if (failures >= MAX_FAILED_READS) {
          stop()
          setCamera('no_reader')
          return
        }
      }
      timer = window.setTimeout(() => void scan(video, reader), SCAN_GAP_MS)
    }

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) return setCamera('unavailable')
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        })
      } catch (err) {
        if (!stopped) setCamera(err instanceof DOMException && err.name === 'NotAllowedError' ? 'denied' : 'unavailable')
        return
      }
      const video = videoRef.current
      // Panel se mezitim zavrel — kameru hned zase vypnout.
      if (stopped || !video) return stop()
      video.srcObject = stream
      await video.play().catch(() => undefined)
      let reader: CodeReader
      try {
        reader = await createCodeReader()
      } catch {
        stop()
        return setCamera('no_reader')
      }
      if (stopped) return
      setCamera('scanning')
      void scan(video, reader)
    }

    void start()
    return stop
  }, [])

  function submit(e: FormEvent) {
    e.preventDefault()
    // Panel je v portalu, ale React udalost submit posila dal po stromu —
    // formular sablony pod nim by se jinak ulozil taky.
    e.stopPropagation()
    const code = normalizeBarcode(manual)
    if (!code) return setError('Tohle není platný čárový kód. Zkontroluj číslice pod čárami.')
    onCodeRef.current(code)
  }

  const live = camera === 'starting' || camera === 'scanning'
  return (
    <div className="stack">
      <div className="scanner" hidden={!live}>
        <video ref={videoRef} className="scanner-video" muted playsInline aria-label="Náhled kamery" />
        {camera === 'scanning' && <span className="scanner-guide" aria-hidden="true" />}
      </div>
      <Paragraph tone={live ? undefined : 'fg'}>{CAMERA_TEXT[camera]}</Paragraph>
      <form className="inline-editor" onSubmit={submit}>
        <TextField
          label="Číslo pod kódem"
          value={manual}
          onChange={(e) => {
            setManual(e.target.value)
            setError(null)
          }}
          inputMode="numeric"
          autoComplete="off"
          placeholder="8594001234567"
          maxLength={20}
        />
        <Button type="submit">Použít</Button>
      </form>
      {error && <Paragraph tone="error">{error}</Paragraph>}
    </div>
  )
}
